import { useState } from 'react';
import { ShieldCheck, KeyRound } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// Full-screen second-factor challenge: shown after password sign-in whenever
// a verified TOTP factor demands elevation (or a firm re-prompt window has
// lapsed). Verification elevates the session to aal2; the auth listener then
// loads the profile and unmounts this screen.
export default function MfaChallengeScreen() {
  const { verifyMfa, signOut, refreshMfaGate, mfaRequirements } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [backupDone, setBackupDone] = useState(false);

  const backupAllowed = mfaRequirements?.backup_codes_allowed !== false;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const { error: verifyError } = await verifyMfa(code.trim());
    if (verifyError) {
      setError(verifyError.message || 'That code was not accepted.');
      setBusy(false);
    }
    // On success the MFA_CHALLENGE_VERIFIED auth event advances the app.
  };

  const handleBackup = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token ?? '';
      const res = await fetch('/.netlify/functions/mfa-recovery', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: backupCode.trim() }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'That backup code was not accepted.');
      setBackupDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That backup code was not accepted.');
      setBusy(false);
    }
  };

  if (backupDone) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 max-w-md w-full p-6 space-y-4" data-testid="mfa-recovery-done">
          <div className="flex items-center gap-3">
            <KeyRound className="w-6 h-6 text-green-700" />
            <h1 className="text-lg font-bold text-gray-900">Authenticator removed</h1>
          </div>
          <p className="text-sm text-gray-600">
            Your backup code was accepted and the old authenticator was removed from your account.
            Set up a new one now from Account Settings → Security — or immediately, if your firm requires it.
          </p>
          <button
            onClick={async () => {
              // The factors were deleted server-side; the cached session still
              // lists them, so an assessment now would demand a challenge no
              // authenticator can answer. Mint a fresh token first. (Observed
              // on staging 2026-08-02.)
              try { await supabase.auth.refreshSession(); } catch { /* reassess anyway */ }
              refreshMfaGate();
            }}
            data-testid="mfa-recovery-continue"
            className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md border border-gray-200 max-w-md w-full p-6" data-testid="mfa-challenge-screen">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-6 h-6 text-blue-700" />
          <h1 className="text-lg font-bold text-gray-900">Two-factor verification</h1>
        </div>

        {!useBackup ? (
          <>
            <p className="text-sm text-gray-600 mb-5">Enter the 6-digit code from your authenticator app.</p>
            <form onSubmit={handleVerify} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                required
                placeholder="123456"
                data-testid="mfa-challenge-code"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg tracking-widest font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                data-testid="mfa-challenge-submit"
                className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {busy ? 'Verifying…' : 'Verify'}
              </button>
            </form>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-5">
              Enter one of your backup codes. It works once and removes your current authenticator, so you can enroll a new one.
            </p>
            <form onSubmit={handleBackup} className="space-y-4">
              <input
                type="text"
                autoComplete="off"
                value={backupCode}
                onChange={e => setBackupCode(e.target.value)}
                required
                placeholder="backup code"
                data-testid="mfa-backup-code-input"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-center font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={busy || backupCode.trim().length === 0}
                data-testid="mfa-backup-code-submit"
                className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {busy ? 'Checking…' : 'Use backup code'}
              </button>
            </form>
          </>
        )}

        {error && (
          <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="mfa-challenge-error">{error}</p>
        )}

        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
          {backupAllowed ? (
            <button
              onClick={() => { setUseBackup(v => !v); setError(''); }}
              data-testid="mfa-use-backup-code"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {useBackup ? 'Use authenticator code' : 'Use a backup code'}
            </button>
          ) : (
            <span className="text-xs text-gray-400">Lost your authenticator? Contact your firm admin.</span>
          )}
          <button
            onClick={() => void signOut()}
            data-testid="mfa-signout"
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
